import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeftRight, Check, Copy, Volume2 } from 'lucide-react';
import { TranslationService } from '../services/translationService';
import { speechService } from '../services/speechService';
import { usePageMeta, SITE_NAME } from '../lib/usePageMeta';

const commonPhrases = [
  { en: "Hello, how are you?", es: "Hola, ¿cómo estás?", fr: "Bonjour, comment allez-vous?" },
  { en: "Thank you very much", es: "Muchas gracias", fr: "Merci beaucoup" },
  { en: "Where is the bathroom?", es: "¿Dónde está el baño?", fr: "Où sont les toilettes?" },
  { en: "I don't understand", es: "No entiendo", fr: "Je ne comprends pas" },
  { en: "Can you help me?", es: "¿Puedes ayudarme?", fr: "Pouvez-vous m'aider?" },
  { en: "How much does it cost?", es: "¿Cuánto cuesta?", fr: "Combien ça coûte?" }
];

const selectClass =
  'h-11 w-full min-w-0 appearance-none rounded-md border border-rule bg-surface px-3 pr-8 text-ink transition-colors hover:border-ink/40 focus-visible:border-leaf sm:w-48';

const IconButton = ({ label, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="grid h-10 w-10 place-items-center rounded-md text-ink-soft transition-colors hover:bg-ink/5 hover:text-ink"
    aria-label={label}
    title={label}
  >
    {children}
  </button>
);

const LanguageSelect = ({ id, label, value, onChange, languages }) => (
  <div className="relative w-full sm:w-auto">
    <label htmlFor={id} className="sr-only">{label}</label>
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className={selectClass}>
      {languages.map((lang) => (
        <option key={lang.code} value={lang.code}>{lang.name}</option>
      ))}
    </select>
    <svg className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-soft" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  </div>
);

const TranslationsPage = () => {
  const [searchParams] = useSearchParams();
  const initialText = searchParams.get('text') || '';
  const [sourceText, setSourceText] = useState(initialText);
  const [translatedText, setTranslatedText] = useState('');
  const [sourceLang, setSourceLang] = useState(initialText ? 'en' : 'auto');
  const [targetLang, setTargetLang] = useState('es');
  const [isTranslating, setIsTranslating] = useState(false);
  const [detectedLang, setDetectedLang] = useState('');
  const [confidence, setConfidence] = useState(0);
  const [copied, setCopied] = useState(false);

  const languages = TranslationService.getSupportedLanguages();

  usePageMeta(
    `Translation: Translate Words and Phrases | ${SITE_NAME}`,
    'Translate English words and phrases into Spanish, French, German and more, then listen to how they sound.'
  );

  const runTranslation = async (text, from, to) => {
    setIsTranslating(true);
    setDetectedLang('');
    setConfidence(0);

    try {
      const result = await TranslationService.translateText(text, from, to);
      setTranslatedText(result.translatedText);
      setDetectedLang(result.sourceLanguage);
      setConfidence(result.confidence);
    } catch (error) {
      console.error('Translation failed:', error);

      // Explain the failure in the language the visitor asked for
      const errorMessages = {
        'es': `No se pudo traducir "${text}". Prueba con una frase más corta.`,
        'fr': `Impossible de traduire "${text}". Essayez une phrase plus courte.`,
        'de': `"${text}" konnte nicht übersetzt werden. Versuchen Sie einen kürzeren Satz.`,
        'it': `Impossibile tradurre "${text}". Prova una frase più breve.`,
      };

      setTranslatedText(
        errorMessages[to] ||
        `"${text}" couldn't be translated. Try a shorter phrase, like "hello" or "thank you".`
      );
    } finally {
      setIsTranslating(false);
    }
  };

  // Arriving from a word page (?text=serendipity) translates straight away
  useEffect(() => {
    if (initialText) runTranslation(initialText, 'en', targetLang);
  }, []);

  const handleTranslate = () => {
    if (!sourceText.trim()) return;
    runTranslation(sourceText, sourceLang, targetLang);
  };

  const swapLanguages = () => {
    if (sourceLang !== 'auto') {
      setSourceLang(targetLang);
      setTargetLang(sourceLang);
      setSourceText(translatedText);
      setTranslatedText(sourceText);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy text:', error);
    }
  };

  const speakText = async (text, lang) => {
    try {
      await speechService.speak(text, { lang, rate: 0.8 });
    } catch (error) {
      console.error('Speech failed:', error);
    }
  };

  const translatePhrase = (phrase) => {
    setSourceText(phrase);
    setSourceLang('en');
    runTranslation(phrase, 'en', targetLang);
  };

  return (
    <div className="wrap pt-12 md:pt-16">
      <h1 className="font-serif text-4xl font-semibold tracking-[-0.01em] md:text-5xl">Translation</h1>
      <p className="mt-3 max-w-xl text-lg text-ink-soft">
        Translate a word or a short phrase, then listen to how it sounds.
      </p>

      <div className="mt-10 overflow-hidden rounded-md border border-rule bg-surface">
        <div className="flex flex-col gap-3 border-b border-rule p-4 sm:flex-row sm:items-center">
          <LanguageSelect id="source-lang" label="Translate from" value={sourceLang} onChange={setSourceLang} languages={languages} />
          <button
            type="button"
            onClick={swapLanguages}
            disabled={sourceLang === 'auto'}
            className="grid h-11 w-11 shrink-0 place-items-center self-center rounded-md border border-rule text-ink transition-colors hover:border-ink disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Swap languages"
            title={sourceLang === 'auto' ? 'Pick a source language to swap' : 'Swap languages'}
          >
            <ArrowLeftRight size={18} aria-hidden="true" />
          </button>
          <LanguageSelect
            id="target-lang"
            label="Translate to"
            value={targetLang}
            onChange={setTargetLang}
            languages={languages.filter((lang) => lang.code !== 'auto')}
          />
          {detectedLang && (
            <p className="text-sm text-ink-soft sm:ml-auto">
              Read as {TranslationService.getLanguageName(detectedLang)}
              {confidence > 0 && `, ${Math.round(confidence * 100)}% confident`}
            </p>
          )}
        </div>

        <div className="grid md:grid-cols-2">
          <div className="flex flex-col border-b border-rule p-4 md:border-b-0 md:border-r">
            <label htmlFor="source-text" className="sr-only">Text to translate</label>
            <textarea
              id="source-text"
              value={sourceText}
              onChange={(e) => setSourceText(e.target.value)}
              placeholder="Type or paste text"
              className="h-56 w-full resize-none bg-transparent font-serif text-xl leading-relaxed text-ink outline-none placeholder:text-ink-soft/70 focus-visible:outline-none"
              maxLength={5000}
            />
            <div className="mt-3 flex items-center justify-between gap-2">
              <span className="text-sm text-ink-soft">{sourceText.length}/5000</span>
              <div className="flex items-center gap-1">
                {sourceText && (
                  <IconButton label="Listen to the text" onClick={() => speakText(sourceText, sourceLang === 'auto' ? 'en' : sourceLang)}>
                    <Volume2 size={18} aria-hidden="true" />
                  </IconButton>
                )}
                <button
                  type="button"
                  onClick={handleTranslate}
                  disabled={!sourceText.trim() || isTranslating}
                  className="h-10 rounded-md bg-ink px-5 font-medium text-paper transition-colors hover:bg-ink/85 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {isTranslating ? 'Translating…' : 'Translate'}
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-col bg-paper/60 p-4" aria-live="polite">
            <div className="h-56 overflow-y-auto">
              {isTranslating ? (
                <p className="text-ink-soft">Translating…</p>
              ) : (
                <p className={`font-serif text-xl leading-relaxed ${translatedText ? 'text-ink' : 'text-ink-soft/70'}`}>
                  {translatedText || 'The translation appears here.'}
                </p>
              )}
            </div>
            {translatedText && !isTranslating && (
              <div className="mt-3 flex items-center justify-end gap-1">
                <IconButton label={copied ? 'Copied' : 'Copy the translation'} onClick={() => copyToClipboard(translatedText)}>
                  {copied ? <Check size={18} aria-hidden="true" /> : <Copy size={18} aria-hidden="true" />}
                </IconButton>
                <IconButton label="Listen to the translation" onClick={() => speakText(translatedText, targetLang)}>
                  <Volume2 size={18} aria-hidden="true" />
                </IconButton>
              </div>
            )}
          </div>
        </div>
      </div>

      <section className="mt-16">
        <h2 className="font-serif text-2xl font-semibold">Common phrases</h2>
        <p className="mt-2 text-ink-soft">Pick one to translate it into the language you chose above.</p>
        <ul className="mt-6 divide-y divide-rule border-y border-rule">
          {commonPhrases.map((phrase) => (
            <li key={phrase.en} className="grid gap-2 py-4 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)] md:items-center md:gap-6">
              <button
                type="button"
                onClick={() => translatePhrase(phrase.en)}
                className="rounded-sm text-left font-serif text-xl text-ink decoration-mark decoration-4 underline-offset-4 hover:underline"
              >
                {phrase.en}
              </button>
              {[
                { lang: 'es', name: 'Spanish', text: phrase.es },
                { lang: 'fr', name: 'French', text: phrase.fr },
              ].map(({ lang, name, text }) => (
                <div key={lang} className="flex items-center gap-1 text-ink-soft">
                  <span className="min-w-0 flex-1">
                    <span className="sr-only">{name}: </span>{text}
                  </span>
                  <IconButton label={`Listen in ${name}`} onClick={() => speakText(text, lang)}>
                    <Volume2 size={16} aria-hidden="true" />
                  </IconButton>
                </div>
              ))}
            </li>
          ))}
        </ul>
        <p className="mt-6 text-sm text-ink-soft">
          Translations come from the free MyMemory service and may be less accurate for long or technical text.
        </p>
      </section>
    </div>
  );
};

export default TranslationsPage;
